import type { components } from "@brandex/api-client";
import { useBrandex } from "@brandex/dashboard";
import { Alert, Badge, Button, Input, Spinner } from "@brandex/ui";
import { useQuery } from "@tanstack/react-query";
import { PenSquare, Search } from "lucide-react";
import { useEffect, useState } from "react";

import { can, useStaffAccess } from "../access";
import { QueryError } from "../views";
import { MANAGE_TABS, type ManageTab } from "./client-manage";

type ClientRow = components["schemas"]["ClientRowDto"];

/** Clients a staff member can pick from: every client, or only their assigned ones (the server filters). */
export function useManageableClients(search = "") {
  const { api } = useBrandex();
  return useQuery<ClientRow[]>({
    queryKey: ["admin", "clients", "manageable", search],
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/admin/clients", { params: { query: { limit: 100, ...(search === "" ? {} : { search }) } } });
      if (data === undefined) throw new Error("Empty response");
      return data.items;
    },
  });
}

/** Only onboarded clients have a practice to maintain. */
export const manageable = (r: ClientRow) => r.specialty !== null && r.specialty !== undefined;

/**
 * Client content (ADR 0020/0022): pick a client, then edit their profile,
 * portfolio, articles, brand kit, bookings and website. Super admins see
 * every client; account managers see the clients assigned to them.
 */
export function ContentPage() {
  const { nav } = useBrandex();
  const access = useStaffAccess();
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearch(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);
  const clients = useManageableClients(search);
  const everyone = can(access.data, "platform.clients.manage");
  const open = (r: ClientRow, tab: ManageTab) => nav.navigate(`/clients/${r.organization_id}/manage?tab=${tab}`);

  return (
    <div className="grid gap-6">
      <div className="grid gap-1">
        <h1 className="flex items-center gap-2 font-display text-2xl font-bold">
          <PenSquare className="h-6 w-6" aria-hidden />
          Client content
        </h1>
        <p className="text-sm text-muted-foreground">
          {everyone
            ? "Choose a client to maintain their website: profile and portfolio, articles, brand kit, bookings and website settings."
            : "The clients assigned to you. Choose one to maintain their website content."}
        </p>
      </div>
      <label className="relative max-w-md">
        <span className="sr-only">Search clients</span>
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <Input className="pl-9" placeholder="Search by doctor or practice" value={query} onChange={(e) => setQuery(e.target.value)} />
      </label>
      {clients.isPending ? (
        <Spinner label="Loading" />
      ) : clients.isError ? (
        <QueryError error={clients.error} />
      ) : clients.data.length === 0 ? (
        <Alert tone="info">{search !== "" ? "No client matches that search." : everyone ? "There are no clients yet." : "No clients are assigned to you yet. A super admin assigns them from a client's Manage page."}</Alert>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {clients.data.map((r) => (
            <li key={r.organization_id} className="grid gap-3 rounded-[var(--radius-lg)] border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-semibold">{r.doctor_name ?? r.organization_name}</p>
                  <p className="truncate text-xs text-muted-foreground">{r.website_slug ?? r.organization_name}</p>
                </div>
                {manageable(r) ? <Badge tone="success">Onboarded</Badge> : <Badge tone="warning">Not onboarded</Badge>}
              </div>
              {manageable(r) ? (
                <div className="flex flex-wrap gap-2">
                  {MANAGE_TABS.map((t) => (
                    <Button key={t.key} size="sm" variant={t.key === "profile" ? "primary" : "outline"} onClick={() => open(r, t.key)} aria-label={`${t.label}: ${r.doctor_name ?? r.organization_name}`}>
                      {t.label}
                    </Button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">This client hasn't finished onboarding, so there is no website content yet.</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
